'use client';

import { RotateCcw, UserRound, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

/**
 * The bulk action bar for hand-over and take-back (EH-002, EH-005).
 *
 * **Both directions sit here on purpose.** Bulk-set without bulk-revert is a
 * one-click action with a fifteen-click undo, which is an asymmetry the user pays
 * for every time they change their mind — and the endpoint serving the set
 * direction already serves the revert, so the cost of symmetry is a second button.
 */
export function HandlingBulkBar({
  selectedCount,
  allSelected,
  someSelected,
  onToggleAll,
  onClear,
  onHandOver,
  onTakeBack,
  isPending = false
}: {
  selectedCount: number;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  onClear: () => void;
  onHandOver: () => void;
  onTakeBack: () => void;
  isPending?: boolean;
}) {
  return (
    <div
      className="bg-muted/60 mb-2 flex items-center gap-3 rounded-md border px-3 py-2"
      data-testid="handling-bulk-bar"
    >
      <Checkbox
        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
        onCheckedChange={onToggleAll}
        aria-label="Select all institutions"
        data-testid="handling-select-all"
      />
      <span className="text-sm font-medium">{selectedCount} selected</span>
      <div className="ml-auto flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onHandOver}
          disabled={selectedCount === 0 || isPending}
          data-testid="handling-bulk-hand-over"
        >
          <UserRound className="mr-1.5 h-3.5 w-3.5" />
          Hand over
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onTakeBack}
          disabled={selectedCount === 0 || isPending}
          data-testid="handling-bulk-take-back"
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          Take back into Vale
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClear}
          disabled={isPending}
          aria-label="Clear selection"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
